import { useState } from "react";
import { useNotifications, NotificationType, NotificationPriority, NotificationChannel } from "@/contexts/NotificationContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  Bell, CheckCheck, Trash2, AlertTriangle, ShieldCheck, Package,
  FileText, CreditCard, Truck, Info, Check, Settings, ExternalLink,
  Mail, Smartphone
} from "lucide-react";

const TYPE_ICONS: Record<NotificationType, React.ReactNode> = {
  gst_deadline: <ShieldCheck className="h-4 w-4" />,
  low_inventory: <Package className="h-4 w-4" />,
  unpaid_invoice: <FileText className="h-4 w-4" />,
  subscription_renewal: <CreditCard className="h-4 w-4" />,
  eway_expiry: <Truck className="h-4 w-4" />,
  system: <Info className="h-4 w-4" />,
  approval: <Check className="h-4 w-4" />,
};

const TYPE_LABELS: Record<NotificationType, string> = {
  gst_deadline: "GST Deadline", low_inventory: "Low Inventory", unpaid_invoice: "Unpaid Invoice",
  subscription_renewal: "Subscription", eway_expiry: "E-Way Bill", system: "System", approval: "Approval",
};

const PRIORITY_STYLES: Record<NotificationPriority, string> = {
  critical: "text-destructive", high: "text-orange-500", medium: "text-primary", low: "text-muted-foreground",
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function NotificationsPage() {
  const {
    notifications, preferences, unreadCount, criticalCount,
    markAsRead, markAllAsRead, dismiss, dismissAll, updatePreference,
  } = useNotifications();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState("all");
  const [filterType, setFilterType] = useState<"all" | NotificationType>("all");

  const visible = notifications.filter((n) => !n.isDismissed);
  const filtered = filterType === "all" ? visible : visible.filter((n) => n.type === filterType);
  const tabFiltered = tab === "all" ? filtered : tab === "unread" ? filtered.filter((n) => !n.isRead) : filtered.filter((n) => n.priority === "critical");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">Notifications</h1>
          <p className="text-sm text-muted-foreground">Alerts, reminders & system notifications</p>
        </div>
        <div className="flex gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { markAllAsRead(); toast({ title: "All marked as read" }); }}>
              <CheckCheck className="h-3.5 w-3.5" /> Mark All Read
            </Button>
          )}
          <Button variant="outline" size="sm" className="gap-1.5 text-destructive" onClick={() => { dismissAll(); toast({ title: "All dismissed" }); }}>
            <Trash2 className="h-3.5 w-3.5" /> Clear All
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardDescription>Unread</CardDescription></CardHeader>
          <CardContent><div className="text-2xl font-bold text-foreground">{unreadCount}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Critical</CardDescription></CardHeader>
          <CardContent><div className="text-2xl font-bold text-destructive">{criticalCount}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Email Queued</CardDescription></CardHeader>
          <CardContent><div className="text-2xl font-bold text-foreground">{visible.filter((n) => n.channels.includes("email") && !n.isRead).length}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Mobile Alerts</CardDescription></CardHeader>
          <CardContent><div className="text-2xl font-bold text-foreground">{visible.filter((n) => n.channels.includes("mobile") && !n.isRead).length}</div></CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <TabsList>
            <TabsTrigger value="all" className="gap-1.5"><Bell className="h-3.5 w-3.5" /> All ({visible.length})</TabsTrigger>
            <TabsTrigger value="unread" className="gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Unread ({visible.filter((n) => !n.isRead).length})</TabsTrigger>
            <TabsTrigger value="critical" className="gap-1.5">Critical ({visible.filter((n) => n.priority === "critical").length})</TabsTrigger>
            <TabsTrigger value="preferences" className="gap-1.5"><Settings className="h-3.5 w-3.5" /> Preferences</TabsTrigger>
          </TabsList>

          {tab !== "preferences" && (
            <div className="flex gap-1.5 flex-wrap">
              <Button variant={filterType === "all" ? "default" : "outline"} size="sm" className="text-xs h-7" onClick={() => setFilterType("all")}>All</Button>
              {(Object.keys(TYPE_LABELS) as NotificationType[]).map((t) => (
                <Button key={t} variant={filterType === t ? "default" : "outline"} size="sm" className="text-xs h-7 gap-1" onClick={() => setFilterType(t)}>
                  {TYPE_ICONS[t]} {TYPE_LABELS[t]}
                </Button>
              ))}
            </div>
          )}
        </div>

        <TabsContent value="all"><NotificationList items={tabFiltered} markAsRead={markAsRead} dismiss={dismiss} navigate={navigate} /></TabsContent>
        <TabsContent value="unread"><NotificationList items={tabFiltered} markAsRead={markAsRead} dismiss={dismiss} navigate={navigate} /></TabsContent>
        <TabsContent value="critical"><NotificationList items={tabFiltered} markAsRead={markAsRead} dismiss={dismiss} navigate={navigate} /></TabsContent>

        <TabsContent value="preferences">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Notification Preferences</CardTitle>
              <CardDescription>Control how you receive alerts for each type</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Alert Type</TableHead>
                    <TableHead className="text-center"><div className="flex items-center justify-center gap-1"><Bell className="h-3.5 w-3.5" /> Dashboard</div></TableHead>
                    <TableHead className="text-center"><div className="flex items-center justify-center gap-1"><Mail className="h-3.5 w-3.5" /> Email</div></TableHead>
                    <TableHead className="text-center"><div className="flex items-center justify-center gap-1"><Smartphone className="h-3.5 w-3.5" /> Mobile</div></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preferences.map((pref) => (
                    <TableRow key={pref.type}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {TYPE_ICONS[pref.type]}
                          <span className="font-medium text-foreground">{pref.label}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch checked={pref.dashboard} onCheckedChange={(v) => updatePreference(pref.type, "dashboard", v)} />
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch checked={pref.email} onCheckedChange={(v) => updatePreference(pref.type, "email", v)} />
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch checked={pref.mobile} onCheckedChange={(v) => updatePreference(pref.type, "mobile", v)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-xs text-muted-foreground mt-4">
                Note: Email and mobile alerts require Lovable Cloud to be enabled for actual delivery. Dashboard alerts are always available.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function NotificationList({ items, markAsRead, dismiss, navigate }: {
  items: any[];
  markAsRead: (id: string) => void;
  dismiss: (id: string) => void;
  navigate: (path: string) => void;
}) {
  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <Bell className="h-8 w-8 mx-auto mb-3 opacity-30" />
          <p>No notifications to show</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        {items.map((n) => (
          <div
            key={n.id}
            className={`flex items-start gap-3 px-5 py-4 border-b border-border/50 hover:bg-muted/30 transition-colors ${
              !n.isRead ? "bg-primary/5" : ""
            }`}
          >
            <div className={`mt-0.5 shrink-0 ${PRIORITY_STYLES[n.priority as NotificationPriority]}`}>
              {TYPE_ICONS[n.type as NotificationType]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className={`text-sm ${!n.isRead ? "font-semibold text-foreground" : "text-foreground/80"}`}>{n.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{n.message}</p>
                </div>
                <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">{timeAgo(n.createdAt)}</span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="text-[10px] h-4">{TYPE_LABELS[n.type as NotificationType]}</Badge>
                <Badge variant={n.priority === "critical" ? "destructive" : n.priority === "high" ? "default" : "secondary"} className="text-[10px] h-4 capitalize">{n.priority}</Badge>
                {n.channels.map((ch: string) => (
                  <Badge key={ch} variant="outline" className="text-[9px] h-4 capitalize">{ch}</Badge>
                ))}
                <div className="flex-1" />
                {n.actionUrl && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs gap-1" onClick={() => navigate(n.actionUrl!)}>
                    {n.actionLabel || "View"} <ExternalLink className="h-3 w-3" />
                  </Button>
                )}
                {!n.isRead && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs gap-1" onClick={() => markAsRead(n.id)}>
                    <Check className="h-3 w-3" /> Read
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="h-6 text-xs gap-1 text-destructive" onClick={() => dismiss(n.id)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
